import { useMemo, useState, type ReactNode } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarPlus,
  CircleDollarSign,
  FileCheck2,
  Info,
  LockKeyhole
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  PERFORMANCE_METRIC_KEYS,
  type PerformanceLoanDetail,
  type PerformanceMetricKey,
  type PipelinePerformanceViewModel
} from './types';
import { formatCompactCurrency, formatCurrency, formatDateRange, formatPercent } from './formatters';

interface PerformanceOverviewProps {
  model: PipelinePerformanceViewModel;
  onOpenLoans: (title: string, description: string, loans: PerformanceLoanDetail[]) => void;
}

type ChartMode = 'units' | 'volume';

const METRIC_META: Record<PerformanceMetricKey, {
  label: string;
  definition: string;
  icon: ReactNode;
  className: string;
}> = {
  created: {
    label: 'Created',
    definition: 'Encompass loan creation date (field 2025).',
    icon: <CalendarPlus className="h-4 w-4" />,
    className: 'text-chart-1'
  },
  application: {
    label: 'Applications',
    definition: 'Application received date (Encompass field 3142).',
    icon: <FileCheck2 className="h-4 w-4" />,
    className: 'text-chart-2'
  },
  locked: {
    label: 'Locked',
    definition: 'Buy-side lock date, then sell-side lock date, then lock request date.',
    icon: <LockKeyhole className="h-4 w-4" />,
    className: 'text-chart-3'
  },
  funded: {
    label: 'Funded',
    definition: 'Brokered loans use the matched payout fund date when present; all others use the Encompass funded date. Closing date is never used.',
    icon: <CircleDollarSign className="h-4 w-4" />,
    className: 'text-chart-4'
  }
};

function Delta({ value, suffix }: { value: number | null; suffix: string }) {
  if (value === null || !Number.isFinite(value)) {
    return <span className="text-muted-foreground">n/a {suffix}</span>;
  }
  if (value === 0) {
    return <span className="text-muted-foreground tabular-nums">0.0% {suffix}</span>;
  }
  const positive = value > 0;
  return (
    <span className={cn('inline-flex items-center gap-0.5 tabular-nums', positive ? 'text-success' : 'text-destructive')}>
      {positive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
      {formatPercent(value)} {suffix}
    </span>
  );
}

function MetricCard({
  metric,
  model,
  onOpenLoans
}: {
  metric: PerformanceMetricKey;
  model: PipelinePerformanceViewModel;
  onOpenLoans: PerformanceOverviewProps['onOpenLoans'];
}) {
  const summary = model.summaries[metric];
  const meta = METRIC_META[metric];
  const hasComparisonEvents = summary.comparison.units > 0;
  return (
    <Card className="shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 pb-2">
        <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
          {meta.label}
          <TooltipProvider>
            <UiTooltip>
              <TooltipTrigger asChild>
                <button type="button" className="rounded text-muted-foreground hover:text-foreground" aria-label={`${meta.label} definition`}>
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-72">{meta.definition}</TooltipContent>
            </UiTooltip>
          </TooltipProvider>
        </CardTitle>
        <span className={meta.className}>{meta.icon}</span>
      </CardHeader>
      <CardContent className="space-y-2 p-4 pt-0">
        <div>
          <div className="text-2xl font-bold tabular-nums">
            {summary.current.units.toLocaleString()} <span className="text-sm font-medium text-muted-foreground">loans</span>
          </div>
          <div className="text-xs text-muted-foreground">{formatCompactCurrency(summary.current.volume)}</div>
        </div>
        <div className="grid grid-cols-2 gap-x-2 text-xs">
          <div>
            <div className="font-medium tabular-nums">{summary.current.unitsPerDay.toFixed(2)}</div>
            <div className="text-muted-foreground">loans/day</div>
          </div>
          <div>
            <div className="font-medium tabular-nums">{formatCompactCurrency(summary.current.volumePerDay)}</div>
            <div className="text-muted-foreground">volume/day</div>
          </div>
        </div>
        <div className="space-y-0.5 border-t pt-2 text-[11px]">
          {hasComparisonEvents ? (
            <>
              <Delta value={summary.unitsPerDayDeltaPct} suffix="units/day" />
              <div><Delta value={summary.volumePerDayDeltaPct} suffix="volume/day" /></div>
            </>
          ) : (
            <div className="text-muted-foreground">
              {model.comparisonRange.isEmpty ? (
                <div>{model.comparisonRange.label}</div>
              ) : (
                <>
                  <div>No prior-period events</div>
                  <div>vs {formatDateRange(model.comparisonRange.from, model.comparisonRange.to)}</div>
                </>
              )}
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-full justify-start px-0 text-xs text-primary hover:bg-transparent"
          onClick={() => onOpenLoans(
            `${meta.label} loans`,
            model.currentRange.label,
            summary.loans
          )}
        >
          View {summary.current.units.toLocaleString()} contributing loan{summary.current.units === 1 ? '' : 's'}
        </Button>
      </CardContent>
    </Card>
  );
}

function TrendTooltip({
  active,
  label,
  payload,
  mode
}: {
  active?: boolean;
  label?: string | number;
  payload?: Array<{ name?: string; value?: number; payload?: { comparisonLabel?: string } }>;
  mode: ChartMode;
}) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const formatValue = (value: number | undefined) => {
    if (typeof value !== 'number') {
      return '—';
    }
    return mode === 'volume' ? formatCurrency(value) : value.toLocaleString();
  };
  return (
    <div className="min-w-44 rounded-lg border bg-card p-3 text-xs shadow-lg">
      <div className="mb-2 font-medium">{label}</div>
      {payload.map((item) => (
        <div key={item.name} className="flex items-center justify-between gap-4 py-0.5">
          <span className="text-muted-foreground">
            {item.name === 'Comparison' ? `Comparison (${item.payload?.comparisonLabel || 'matching period'})` : item.name}
          </span>
          <span className="font-medium tabular-nums">{formatValue(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function PerformanceOverview({ model, onOpenLoans }: PerformanceOverviewProps) {
  const [metric, setMetric] = useState<PerformanceMetricKey>('funded');
  const [chartMode, setChartMode] = useState<ChartMode>('units');

  const chartRows = useMemo(() => model.trend.map((point) => {
    const values = point.metrics[metric];
    return {
      label: point.label,
      comparisonLabel: point.comparisonLabel,
      Current: point.hasCurrent === false
        ? null
        : chartMode === 'volume'
          ? values.current.volume
          : values.current.units,
      Comparison: point.hasComparison === false || point.comparisonLabel === '—'
        ? null
        : chartMode === 'volume'
          ? values.comparison.volume
          : values.comparison.units
    };
  }), [chartMode, metric, model.trend]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {PERFORMANCE_METRIC_KEYS.map((key) => (
          <MetricCard key={key} metric={key} model={model} onOpenLoans={onOpenLoans} />
        ))}
      </div>

      <Card className="shadow-sm">
        <CardHeader className="gap-3 p-4 pb-2 md:flex-row md:items-center md:justify-between md:space-y-0">
          <div>
            <CardTitle className="text-base">Performance trend</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">Current period aligned with its matching comparison window</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-md border bg-background p-0.5" aria-label="Trend metric">
              {PERFORMANCE_METRIC_KEYS.map((key) => (
                <Button
                  key={key}
                  type="button"
                  variant={metric === key ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2 text-xs"
                  aria-pressed={metric === key}
                  onClick={() => setMetric(key)}
                >
                  {METRIC_META[key].label}
                </Button>
              ))}
            </div>
            <div className="flex rounded-md border bg-background p-0.5" aria-label="Trend value">
              {(['units', 'volume'] as const).map((mode) => (
                <Button
                  key={mode}
                  type="button"
                  variant={chartMode === mode ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 px-2 text-xs capitalize"
                  aria-pressed={chartMode === mode}
                  onClick={() => setChartMode(mode)}
                >
                  {mode}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-1">
          {chartRows.length > 0 ? (
            <div className="h-60 w-full sm:h-72" role="img" aria-label={`${METRIC_META[metric].label} ${chartMode} trend compared with ${model.comparisonRange.label}`}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartRows} margin={{ top: 12, right: 12, left: 0, bottom: 0 }} accessibilityLayer>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    minTickGap={24}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    width={chartMode === 'volume' ? 62 : 36}
                    allowDecimals={false}
                    tickFormatter={(value: number) => chartMode === 'volume' ? formatCompactCurrency(value) : value.toLocaleString()}
                  />
                  <Tooltip content={<TrendTooltip mode={chartMode} />} />
                  <Line
                    type="linear"
                    dataKey="Current"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={2.5}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="linear"
                    dataKey="Comparison"
                    stroke="hsl(var(--chart-4))"
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">No trend data in this range.</div>
          )}
          <div className="mt-1 flex items-center justify-center gap-5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-5 bg-chart-1" />Current</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-5 border-t-2 border-dashed border-chart-4" />Comparison</span>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base">Period breakdown</CardTitle>
          <p className="text-xs text-muted-foreground">Select a metric cell to inspect its exact contributing loans.</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-2 p-3 md:hidden">
            {model.periods.map((row) => (
              <div key={row.key} className="rounded-lg border bg-card p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="font-medium">{row.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {row.businessDays} business {row.businessDays === 1 ? 'day' : 'days'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {PERFORMANCE_METRIC_KEYS.map((key) => {
                    const cell = row.metrics[key];
                    return (
                      <button
                        key={key}
                        type="button"
                        className="min-h-16 rounded-md bg-muted/40 p-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => onOpenLoans(
                          `${METRIC_META[key].label} · ${row.label}`,
                          `${cell.units.toLocaleString()} loans contributing ${formatCurrency(cell.volume)}`,
                          cell.loans
                        )}
                      >
                        <span className="block text-xs text-muted-foreground">{METRIC_META[key].label}</span>
                        <span className="block font-medium tabular-nums">{cell.units.toLocaleString()} · {formatCompactCurrency(cell.volume)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {model.periods.length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground">No events in this range.</div>
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[760px] text-sm">
            <caption className="sr-only">Pipeline performance by period and canonical event</caption>
            <thead>
              <tr className="border-y bg-muted/30 text-left text-xs text-muted-foreground">
                <th className="px-4 py-2.5 font-medium">Period</th>
                <th className="px-3 py-2.5 text-right font-medium">Business days</th>
                {PERFORMANCE_METRIC_KEYS.map((key) => (
                  <th key={key} className="px-3 py-2.5 text-right font-medium">{METRIC_META[key].label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {model.periods.map((row) => (
                <tr key={row.key} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-3 font-medium">{row.label}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{row.businessDays}</td>
                  {PERFORMANCE_METRIC_KEYS.map((key) => {
                    const cell = row.metrics[key];
                    return (
                      <td key={key} className="px-3 py-2 text-right">
                        <button
                          type="button"
                          className="rounded px-1.5 py-1 text-right hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => onOpenLoans(
                            `${METRIC_META[key].label} · ${row.label}`,
                            `${cell.units.toLocaleString()} loans contributing ${formatCurrency(cell.volume)}`,
                            cell.loans
                          )}
                        >
                          <span className="block font-medium tabular-nums">{cell.units.toLocaleString()}</span>
                          <span className="block text-[11px] text-muted-foreground tabular-nums">{formatCompactCurrency(cell.volume)}</span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {model.periods.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No events in this range.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
